import {
  renderCouponCell,
  renderDateCell,
  renderMemberCell,
  renderPlanCell,
} from '@components/charts/membership/analytics-overview/columnCells'
import {
  MembershipIncomingRenewal,
  MembershipRenewalOutcome,
  MembershipRenewalPaymentMethod,
} from '@models/membership/MembershipIncomingRenewal'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { TableColumnsType, Tag, Typography } from 'antd'

const { Text } = Typography

interface GetIncomingRenewalColumnsOptions {
  /**
   * Emits the sortable headers. Off by default: the overview preview renders a
   * fixed top-10 with no `onChange`, so a caret there would never sort
   * anything. The dedicated `IncomingRenewalsPage` opts in explicitly.
   */
  withSorting?: boolean
  /** Appends the Outcome column (the full Renewals page; the preview omits it). */
  withOutcome?: boolean
  /**
   * Fixed, narrow widths so the 7 preview columns fit a card without a
   * horizontal scroll (pair with `tableLayout="fixed"`). Member is clipped.
   */
  compact?: boolean
}

const PAYMENT_LABELS: Partial<Record<MembershipRenewalPaymentMethod, string>> =
  {
    card: 'Card',
    direct_debit: 'Direct debit',
    bank_transfer: 'Bank transfer',
  }

const OUTCOME_META: Record<
  MembershipRenewalOutcome,
  { label: string; color: string }
> = {
  renewed: { label: 'Renewed', color: 'success' },
  payment_failed: { label: 'Payment failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
  awaiting_transfer: { label: 'Awaiting transfer', color: 'warning' },
  upcoming: { label: 'Upcoming', color: 'blue' },
}

const renderPaymentCell = (method: MembershipRenewalPaymentMethod | null) => {
  if (method === 'missing') return <Tag color="warning">Missing</Tag>
  const label = method ? PAYMENT_LABELS[method] : undefined
  return label ? <Text>{label}</Text> : <Text type="secondary">--</Text>
}

const renderOutcomeCell = (outcome: MembershipRenewalOutcome | null) => {
  const meta = outcome ? OUTCOME_META[outcome] : undefined
  return meta ? (
    <Tag color={meta.color}>{meta.label}</Tag>
  ) : (
    <Text type="secondary">--</Text>
  )
}

/**
 * Single-sourced column definitions used by both the overview's
 * `IncomingRenewalTable` preview and the dedicated `IncomingRenewalsPage`, so
 * the two tables stay identical. Fields mirror the real
 * `membership/subscriptions/incoming-renewals` read-model row (ticket 537, API
 * #298). No Status column - a renewal isn't a payment, it has no status - and
 * no row actions on either surface.
 */
const getIncomingRenewalColumns = ({
  withSorting = false,
  withOutcome = false,
  compact = false,
}: GetIncomingRenewalColumnsOptions = {}): TableColumnsType<MembershipIncomingRenewal> => [
  {
    title: 'Date',
    ...(compact && { width: 100 }),
    key: 'date',
    sorter: withSorting,
    render: (record: MembershipIncomingRenewal) => renderDateCell(record.date),
  },
  {
    title: 'Member',
    ...(compact && { width: 190, ellipsis: true }),
    render: (record: MembershipIncomingRenewal) =>
      renderMemberCell(record.member, compact),
  },
  {
    title: 'Plan',
    ...(compact && { width: 110 }),
    render: (record: MembershipIncomingRenewal) => renderPlanCell(record.plan),
  },
  {
    title: 'List price',
    ...(compact && { width: 90 }),
    render: (record: MembershipIncomingRenewal) =>
      record.amount != null ? (
        // The row's own currency, not the page's: the table spans programs,
        // which do not all bill in euros.
        <Text>
          {analyticsCurrency(record.amount, true, record.plan?.currency)}
        </Text>
      ) : (
        <Text type="secondary">--</Text>
      ),
  },
  {
    title: 'Coupon',
    ...(compact && { width: 90 }),
    render: (record: MembershipIncomingRenewal) =>
      renderCouponCell(record.coupon),
  },
  {
    // Nullable here, unlike a transaction's: the next payment amount is unknown
    // on some subscriptions, and `--` is not a free renewal.
    title: 'Net amount',
    ...(compact && { width: 90 }),
    render: (record: MembershipIncomingRenewal) =>
      record.netAmount != null ? (
        <Text strong>
          {analyticsCurrency(record.netAmount, true, record.plan?.currency)}
        </Text>
      ) : (
        <Text type="secondary">--</Text>
      ),
  },
  {
    title: 'Payment',
    ...(compact && { width: 110 }),
    render: (record: MembershipIncomingRenewal) =>
      renderPaymentCell(record.paymentMethod),
  },
  ...(withOutcome
    ? [
        {
          title: 'Outcome',
          render: (record: MembershipIncomingRenewal) =>
            renderOutcomeCell(record.outcome),
        },
      ]
    : []),
]

export { getIncomingRenewalColumns }
